'use client';

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
// import { ImageCarousel } from '@components';
import { ScreenSizeContext } from '@context';
import {
	FileRetrieveResponse,
	FileUploadAPIError,
	FileUploadStatus,
	FileUploadStatusResponse,
	FileUploadValidationError,
	FileUploadValidationErrorMessage,
	UpdateAction,
} from '@types';
import { cn, FileUploadItem, fileUploadMimeTypes, FileValidationSchema } from '@utils';
import { DropZone, FileDropItem, FileTrigger } from 'react-aria-components';
import { ValidationError } from 'yup';

import { Button, ErrorFilled, ImageCarousel, LoadingSpinner, Upload } from '@/componentsQGDS';
import { Description, errorMessageStyle, iconStyle, Label } from './../Field';
import { UploadedFileList } from './UploadedFileList';

const MAX_FILES = 10;

interface FileUploadFieldProps {
	/**
	 * Custom styles of the component
	 */
	className?: string;
	/**
	 * The type of file to accept
	 */
	acceptedFileTypes?: 'image' | 'document' | 'all';
	/**
	 * The value of the file input
	 */
	value: FileUploadItem[];
	/**
	 * onChange function for keeping track field value
	 */
	onChange: (files: FileUploadItem[]) => void;
	/**
	 * label of the input element when there is no external label
	 */
	label?: string;
	/**
	 * Boolean for determining whether this field is required
	 */
	isRequired?: boolean;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	isDisabled?: boolean;
	/**
	 * Boolean for determining whether this field allows multiple files to be selected
	 */
	allowsMultiple?: boolean;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/**
	 * Function to handle file upload
	 * @param file - The file to be uploaded
	 * @param setUploadingFiles - Function to update the state of uploading files
	 * @returns A promise that resolves when the upload is complete
	 */
	uploadFile: (
		file: File,
		abortSignal?: AbortSignal,
		timeout?: number,
		uploadStatusTimeout?: number,
	) => Promise<FileUploadStatusResponse>;
	/**
	 * Function to handle file retrieval
	 * @param file - The file to be retrieved
	 * @returns A promise that resolves when the retrieval is complete
	 */
	retrieveFile?: (fileIdentifier: string) => Promise<FileRetrieveResponse>;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
	className,
	acceptedFileTypes = 'all',
	label,
	isRequired,
	hintText,
	errorMsg,
	isDisabled = false,
	autoFocus,
	allowsMultiple = false,
	value = [],
	uploadFile,
	onChange,
	retrieveFile,
}) => {
	// State
	const { isMdScreen: isTablet } = useContext(ScreenSizeContext);
	const [files, setFiles] = useState<FileUploadItem[]>(value);
	const [isUploading, setIsUploading] = useState(false);
	const [hasMaxFilesError, setHasMaxFilesError] = useState(false);

	// Memo
	const hasError = useMemo(
		() => Boolean(errorMsg) || hasMaxFilesError,
		[errorMsg, hasMaxFilesError],
	);
	const isMaxFilesReached = useMemo(() => files.length >= MAX_FILES, [files.length]);

	// Others
	const abortControllersRef = useRef<Record<string, AbortController>>({});

	// Mutation
	const uploadFileMutation = useMutation({
		mutationFn: (file: File) => {
			const abortController = new AbortController();
			abortControllersRef.current[file.name] = abortController;
			return uploadFile(file, abortController.signal);
		},
		onSuccess: (response, file) => {
			updateFileUploadStatus(file.name, response);
			delete abortControllersRef.current[file.name];
		},
		onError: (error: unknown, file: File) => {
			if (error instanceof Error && error.name !== 'AbortError') {
				handleUploadError(file.name, error);
			}
			delete abortControllersRef.current[file.name];
		},
	});

	// Helpers
	const updateFiles = (updatedFiles: FileUploadItem[]) => {
		setFiles(updatedFiles);
		onChange(updatedFiles);
		setHasMaxFilesError(false); // Clear local error on any change of files
	};

	const validateFile = async (file: File): Promise<string | undefined> => {
		try {
			await FileValidationSchema(acceptedFileTypes).validate(file);
			return undefined;
		} catch (error) {
			if (error instanceof ValidationError) {
				if (error.type === FileUploadValidationError.MAX_FILE_SIZE) {
					return FileUploadValidationErrorMessage.MAX_FILE_SIZE;
				} else if (error.type === FileUploadValidationError.INVALID_FILE_FORMAT) {
					return FileUploadValidationErrorMessage.INVALID_FILE_FORMAT;
				}
			}
			return FileUploadValidationErrorMessage.UNKNOWN_ERROR;
		}
	};

	const updateFileUploadStatus = (fileName: string, response: FileUploadStatusResponse) => {
		setFiles(prevFiles => {
			const updatedFiles = prevFiles.map(item =>
				item.file?.name === fileName
					? {
							...item,
							uploadStatus: response,
							filename: response.fileuri?.split('/').pop() || undefined,
							error: undefined,
							fileLocation: undefined,
						}
					: item,
			);
			onChange(updatedFiles);
			return updatedFiles;
		});
	};

	const handleUploadError = (fileName: string, error: unknown) => {
		setFiles(prevFiles => {
			const updatedFiles = prevFiles.map(item => {
				if (item.file?.name !== fileName) return item;

				const isErrorInstance = error instanceof Error;
				const errorMessage = isErrorInstance ? error.message : '';

				const uploadStatus = {
					status:
						errorMessage === FileUploadAPIError.MALWARE_DETECTED
							? FileUploadStatus.DIRTY
							: FileUploadStatus.FAILED,
					fileuri: null,
					contactid: null,
				};

				let errorType = FileUploadAPIError.UPLOAD_FAILED;
				if (isErrorInstance) {
					if (errorMessage === FileUploadAPIError.CONNECTION_TIMEOUT) {
						errorType = FileUploadAPIError.CONNECTION_TIMEOUT;
					} else if (errorMessage === FileUploadAPIError.MALWARE_DETECTED) {
						errorType = FileUploadAPIError.MALWARE_DETECTED;
					}
				}

				return {
					...item,
					uploadStatus,
					error: errorType,
				};
			});

			onChange(updatedFiles);
			return updatedFiles;
		});
	};

	const handleFilesChange = async (newFiles: File[]) => {
		// Clear existing files first when multiple files are not allowed
		if (!allowsMultiple) {
			updateFiles([]);
		}

		// Ignore the upload if the total number of files exceeds the maximum when multiple files are allowed
		if (allowsMultiple) {
			const isExceedingMax = files.length + newFiles.length > MAX_FILES;
			setHasMaxFilesError(isExceedingMax);
			if (isExceedingMax) {
				return;
			}
		}

		const uniqueNewFiles = newFiles.filter(
			newFile =>
				!files.some(
					existingFile =>
						existingFile.file?.name === newFile.name && existingFile.file?.size === newFile.size,
				),
		);

		if (uniqueNewFiles.length === 0) return;
		
		// For single file, only take the first file
		const filesToProcess = allowsMultiple ? uniqueNewFiles : [uniqueNewFiles[0]];

		const updatedFiles = [
			...(allowsMultiple ? files : []), // Only keep existing files if multiple is allowed
			...filesToProcess.map(file => ({
				file,
				error: undefined,
				action: UpdateAction.CREATE,
			})),
		];
		updateFiles(updatedFiles);

		for (const newFile of filesToProcess) {
			const validationError = await validateFile(newFile);

			if (validationError === undefined) {
				updateFileUploadStatus(newFile.name, {
					status: FileUploadStatus.REQUESTED,
					fileuri: null,
					contactid: null,
				});
				uploadFileMutation.mutate(newFile);
			} else {
				setFiles(prevFiles => {
					const updatedFiles = prevFiles.map(item =>
						item.file?.name === newFile.name ? { ...item, error: validationError } : item,
					);
					onChange(updatedFiles);
					return updatedFiles;
				});
			}
		}
	};

	const handleRemove = (index: number) => {
		if (files) {
			const fileToRemove = files[index];
			if (fileToRemove.file) {
				const abortController = abortControllersRef.current[fileToRemove.file.name];
				if (abortController) {
					abortController.abort();
					delete abortControllersRef.current[fileToRemove.file.name];
				}
			}

			// For non-multiple uploads, just clear everything
			if (!allowsMultiple) {
				updateFiles([]);
				return;
			}

			// For multiple uploads, handle normally
			const updatedFiles = files
				.map((file, i) => {
					if (i === index) {
						if (file.uploadStatus?.status === FileUploadStatus.RESTORED) {
							// Mark restored files for removal
							return { ...file, action: UpdateAction.REMOVE };
						}
						// Remove files with other statuses
						return null;
					}
					return file;
				})
				.filter((file): file is FileUploadItem => file !== null);

			updateFiles(updatedFiles);
		}
	};

	const handleRestore = (file: FileUploadItem) => {
		if (file.action === UpdateAction.REMOVE) {
			const updatedFiles = files.map(item =>
				item === file ? { ...item, action: UpdateAction.NONE } : item,
			);
			updateFiles(updatedFiles);
		}
	};

	// Life Cycle
	useEffect(() => {
		const nextIsUploading = files.some(
			file => file.uploadStatus?.status === FileUploadStatus.REQUESTED,
		);
		setIsUploading(nextIsUploading);
	}, [files]);

	// Layout
	return (
		<div className={cn('flex flex-col gap-4', className)}>
			{/* Default form label (optional) */}
			{(label || hintText) && (
				<div className="flex flex-col gap-1">
					{label && <Label isRequired={isRequired}>{label}</Label>}
					{hintText && <Description>{hintText}</Description>}
					{hasError && (
						<p className={cn(errorMessageStyle({ theme: 'light' }))}>
							<ErrorFilled className={iconStyle({ theme: 'light', isInvalid: true })} />
							{errorMsg || (hasMaxFilesError ? `You can upload up to ${MAX_FILES} files` : '')}
						</p>
					)}
				</div>
			)}
			{/* Main file upload field */}
			<DropZone
				className={({ isDropTarget }) =>
					cn([
						`border-light-border-alt relative flex flex-col items-center gap-4 rounded-xs border-2 border-dashed p-6 text-center lg:gap-6 lg:p-8`,
						`${hasError && 'border-status-error bg-status-error-lightest'}`,
						'focus-visible:outline-light-focus focus-visible:outline-3 focus-visible:outline-offset-2',
						`${isDropTarget && 'border-light-action-primary-hover bg-neutral-lightest'}`,
					])
				}
				isDisabled={isDisabled || isMaxFilesReached}
				onDrop={async e => {
					try {
						const fileItems = e.items.filter(file => file.kind === 'file') as FileDropItem[];
						const resolvedFiles = (
							await Promise.all(
								fileItems.map(file =>
									file.getFile().catch(() => null),
								),
							)
						).filter((f): f is File => f !== null && f.type !== '' && f.size > 0);
						if (resolvedFiles.length > 0) {
							handleFilesChange(resolvedFiles);
						}
					} catch {
						// Silently ignore unsupported drops (e.g. folders)
					}
				}}
				aria-labelledby="file-upload-hint"
				aria-describedby="file-upload-instructions"
			>
				{({ isDisabled, isDropTarget }) => (
					<>
						{/* Trailing icons */}
						{isUploading && (
							<LoadingSpinner label="Uploading..." className={`${!isTablet && 'h-16'}`} />
						)}
						{!isUploading && !isTablet && (
							<div
								className={cn([
									`bg-neutral-lightest flex h-16 w-16 items-center justify-center rounded-full p-1`,
									`${isDropTarget && 'bg-transparent'} ${hasError && 'bg-status-error-lighter'}`,
								])}
							>
								<Upload
									className={`${
										isDisabled
											? 'text-light-text-lighter'
											: hasError
												? 'text-status-error'
												: 'text-light-action-secondary'
									}`}
								/>
							</div>
						)}
						<div className="flex flex-col gap-4">
							{/* Heading */}
							<h4
								className={`lg:text-md text-sm ${isDisabled && 'text-light-text-lighter'}`}
								id="file-upload-instructions"
							>
								{isTablet
									? 'Select files to upload'
									: 'Drag and drop files here or select files to upload'}
							</h4>
							{/* Description */}
							<small className="text-light-text-lighter">
								{acceptedFileTypes === 'image'
									? 'You can upload jpg, jpeg, and png files.'
									: acceptedFileTypes === 'document'
										? 'You can upload doc, docx, pdf, and txt files.'
										: 'You can upload doc, docx, pdf, txt, jpg, jpeg, and png files.'}
								<br />
								Files can&apos;t be larger than 20 MB.
								<br />
								You can upload up to {allowsMultiple ? `${MAX_FILES} files` : '1 file'}.
							</small>
						</div>
						<FileTrigger
							allowsMultiple={allowsMultiple}
							acceptedFileTypes={fileUploadMimeTypes[acceptedFileTypes]}
							onSelect={(e: FileList | null) => {
								if (e) {
									handleFilesChange(Array.from(e));
								}
							}}
						>
							{/* Select files button */}
							<Button
								variant="secondary"
								className="w-40"
								isDisabled={isDisabled}
								autoFocus={autoFocus}
								aria-label="Select files to upload"
							>
								{allowsMultiple ? 'Select files' : 'Select a file'}
							</Button>
						</FileTrigger>
					</>
				)}
			</DropZone>
			{files?.length > 0 && (
				<div>
					<UploadedFileList
						files={files}
						isDisabled={isDisabled}
						handleRemove={handleRemove}
						handleRestore={handleRestore}
						uploadFileMutation={uploadFileMutation}
						updateFileUploadStatus={updateFileUploadStatus}
					/>
					<div className="h-4" />
					<ImageCarousel files={files} updateFiles={updateFiles} retrieveFile={retrieveFile} />
				</div>
			)}
		</div>
	);
};
